import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { generateActionId } from "@lib/utils/idGeneration";
import * as Sentry from "@lib/telemetry/sentry";
import {
  actionIsIdentical,
  type ActionMessage,
  type IncomingActionMessage,
  type IncomingUnconfirmedActionMessage,
  type Reducer,
  type UnconfirmedActionMessage,
} from "./core";
import type {
  ClientStorageAdapter,
  ConfirmAction,
  MessageToClient,
  MessageToServer,
  PerformActionFailed,
} from "./network";
import { summarizeMessageToClient } from "./network";
import { type StampableAction, stampActionTimestamp } from "./round/actions";
import type { RoundAggregate } from "./round/aggregate";
import ClientInfoStore from "./round/infoStore/ClientInfoStore";
import { isTransientIndexedDbDisconnectError } from "./storageAdapters/indexedDbErrors";

/** Beat between a transient queue-read failure and its single re-read. */
export const TRANSIENT_IDB_REREAD_DELAY_MS = 1_000;
import {
  StallEpisodeTracker,
  StallError,
  withStallGuard,
} from "@lib/utils/stallGuard";
import { getSyncActivityCount } from "./syncActivityBeacon";
import { undoAction } from "./round/undo";

// Prefix for the notice shown when the server rejects an action and we drop it
// from the queue. We deliberately keep the *verbatim* server reason after this
// frame (rather than a sanitized platitude): the offline funder is an engineer,
// the raw reason is what's useful to act on, and — crucially — the user-facing
// string then matches what we log to Sentry, so a bug report greps straight to
// the log line. The frame's only job is to add the one thing the raw reason
// omits: that the edit was discarded, not retried. Build the full message with
// `discardedActionMessage(reason)`.
// Ceiling on the in-flight action-send map (confirmation-latency signal). Sized
// well above any plausible number of genuinely unconfirmed actions in one page
// load; it exists so a wedged socket can't grow the map for the life of the tab.
const MAX_TRACKED_ACTION_SENDS = 200;

export type ClientRoundSyncEvent = {
  event: "round action confirmed" | "round edit queued offline";
  properties: Record<string, boolean | number | string>;
};

export type CaptureClientRoundSyncEvent = (event: ClientRoundSyncEvent) => void;

export const DISCARDED_ACTION_PREFIX =
  "Couldn't save your change (discarded): ";

export function discardedActionMessage(serverReason: string): string {
  return `${DISCARDED_ACTION_PREFIX}${serverReason}`;
}

const actionSyncInfoEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ACTION_SYNC_DEBUG === "true";

function actionSyncInfo(...args: Parameters<typeof console.info>) {
  if (!actionSyncInfoEnabled) {
    return;
  }

  console.debug(...args);
}

type ActionQueueItem =
  | {
      type: "action";
      action: StampableAction;
      resolve: (result: Error | UnconfirmedActionMessage) => void;
    }
  | {
      type: "undo";
      resolve: (result: Error | UndoRedoInfo | null) => void;
    }
  | {
      type: "redo";
      resolve: (result: Error | UndoRedoInfo | null) => void;
    }
  | {
      type: "handleMessage";
      msg: MessageToClient;
      resolve?: () => void;
    };

export type UndoRedoInfo = {
  message: UnconfirmedActionMessage;
  originalAction: IncomingUnconfirmedActionMessage;
};

export class Client {
  id: string;
  underlyingUserId: string;
  underlyingUserIsSfcAdmin: boolean;
  actingAsUserId: string;
  aggregateId: string;
  reducer: Reducer;
  storage: ClientStorageAdapter;
  infoStore: ClientInfoStore;
  onErrorCallback: (errorMsg: string) => void;
  sendMessage?: (msg: MessageToServer) => void;
  updateCallback?: () => void;
  onActionCallback?: (action: UnconfirmedActionMessage) => void;
  onActionConfirmedCallback?: (action: ActionMessage) => void;
  captureRoundSyncEvent?: CaptureClientRoundSyncEvent;
  offlineMode?: boolean;

  // Actions which we've already retried so we won't retry them again
  private retriedFailedActionIds: Set<string> = new Set();

  // Track the actions taken by this client, since we don't undo actions before that.
  actionsSincePageLoad: Set<string> = new Set();

  // Send time per in-flight action, for the confirmation-latency signal. Only
  // actions FIRST SENT in this page load are tracked: `performance.now()` is
  // page-scoped, so an action drained from the offline queue after a relaunch
  // has no comparable origin and would report a meaningless duration.
  //
  // `retried` covers every way a wait can span more than one transmission
  // attempt: the three client-side resend paths, plus the two the transport owns
  // (an offline-suppressed emit and a retried/capped one), reported in through
  // noteTransportSuppressed / noteTransportRetried. All five client sends go
  // through _sendPerformAction so this can't drift from what was actually sent.
  private actionSends = new Map<
    string,
    {
      sentAt: number;
      retried: boolean;
      /** So the offline-queue event is one per edit, not one per retry press. */
      queuedOfflineReported: boolean;
    }
  >();

  isActionPending = false;
  actionQueue: ActionQueueItem[] = [];

  // Server-message processing runs under a progress watchdog (see the
  // handleMessage case in _startNextPendingAction): windows of this length
  // with NO confirmed-version advance count as a stall. Duration alone is
  // deliberately not the trigger — a long offline catch-up can legitimately
  // apply thousands of actions, and each applied action is progress.
  // Configurable for tests.
  stallWindowMs: number;
  // Once-per-episode latch + probe-window selection, versioned against the
  // confirmed version (see StallEpisodeTracker): one report per stuck
  // operation despite ~1/s server re-delivery, and re-deliveries during an
  // open episode clear the pump on a short probe window instead of each
  // paying the full one.
  private readonly stallEpisode: StallEpisodeTracker;

  constructor({
    id,
    userId,
    aggregateId,
    onErrorCallback,
    onActionConfirmedCallback,
    captureRoundSyncEvent,
    reducer,
    storage,
    clientInfoStore,
    offlineMode,
    underlyingUserIsSfcAdmin = false,
    stallWindowMs = 20_000,
  }: {
    id: string;
    userId: string;
    aggregateId: string;
    onErrorCallback: (errorMsg: string) => void;
    onActionConfirmedCallback?: (action: ActionMessage) => void;
    captureRoundSyncEvent?: CaptureClientRoundSyncEvent;
    reducer: Reducer;
    storage: ClientStorageAdapter;
    clientInfoStore?: ClientInfoStore;
    offlineMode?: boolean;
    underlyingUserIsSfcAdmin?: boolean;
    stallWindowMs?: number;
  }) {
    this.id = id;
    this.actingAsUserId = userId;
    this.underlyingUserId = userId;
    this.underlyingUserIsSfcAdmin = underlyingUserIsSfcAdmin;
    this.aggregateId = aggregateId;
    this.onErrorCallback = onErrorCallback;
    this.onActionConfirmedCallback = onActionConfirmedCallback;
    this.captureRoundSyncEvent = captureRoundSyncEvent;
    this.reducer = reducer;
    this.storage = storage;
    this.offlineMode = offlineMode;
    this.stallWindowMs = stallWindowMs;
    this.stallEpisode = new StallEpisodeTracker(stallWindowMs);
    this.infoStore = clientInfoStore ?? new ClientInfoStore();
  }

  /**
   * The ONE place a PerformAction reaches the transport.
   *
   * Every send and resend goes through here so transmission accounting cannot
   * drift from sending. The previous shape tracked at individual call sites and
   * missed two of the five — the reconnect drain in `sendUnconfirmedActions` and
   * the transient retry — each of which reported an outage-spanning wait as
   * steady-state traffic. `performActionSendSites.test.ts` fails if a new send
   * appears outside this method.
   *
   * `first` starts the wait clock; `repeat` keeps the original timestamp, because
   * the funder's wait began when they made the edit, not when something retried.
   */
  private _sendPerformAction(
    action: IncomingUnconfirmedActionMessage | UnconfirmedActionMessage,
    kind: "first" | "repeat",
  ): void {
    if (!this.sendMessage) return;
    if (kind === "first") this._beginActionWait(action.id);
    else this._noteRetransmission(action.id);
    this.sendMessage({ type: "PerformAction", action });
  }

  /** Start the wait clock for an action's first transmission. */
  private _beginActionWait(actionId: string): void {
    // A session that never receives confirmations (server down, socket wedged)
    // would otherwise grow this map for as long as the tab stays open. Evict
    // oldest-first; Map preserves insertion order.
    if (this.actionSends.size >= MAX_TRACKED_ACTION_SENDS) {
      const oldest = this.actionSends.keys().next();
      if (!oldest.done) this.actionSends.delete(oldest.value);
    }
    if (this.actionSends.has(actionId)) return;
    this.actionSends.set(actionId, {
      sentAt: performance.now(),
      retried: false,
      queuedOfflineReported: false,
    });
  }

  /**
   * Mark that this action was transmitted more than once, or that a transmission
   * was suppressed. Actions with no entry are from a previous page load and stay
   * untracked — the clock is page-scoped, so they have no comparable origin.
   */
  private _noteRetransmission(actionId: string): void {
    const pending = this.actionSends.get(actionId);
    if (pending) pending.retried = true;
  }

  /**
   * The transport did NOT put this action on the wire because the device is
   * offline. Only ImperativeState can know this — `runEmitWithRetry` owns the
   * offline gate, and the engine client cannot see it. Gating the signal on this
   * class's own `offlineMode` produced an event that fired only in tests, because
   * no production caller sets that flag.
   *
   * Reported once per EDIT, not once per suppressed attempt. "Retry Actions" while
   * still offline pushes the whole queue through the transport again and every one
   * is suppressed again, so an event per attempt turned each press into another
   * full queue's worth of "edits queued offline" — a count that grows with the
   * funder's frustration rather than with the number of stuck edits.
   *
   * Untracked actions (carried over from an earlier page load, or evicted from the
   * cap) report nothing: there is no entry to dedupe against, so an event here
   * would be the un-deduped one all over again. Those are already covered, and
   * better, by the round-open pending-edits signal, which is built for exactly the
   * edits that outlive a session.
   */
  public noteTransportSuppressed(actionId: string, actionType: string): void {
    this._noteRetransmission(actionId);
    const pending = this.actionSends.get(actionId);
    if (pending == null || pending.queuedOfflineReported) return;
    pending.queuedOfflineReported = true;
    this.captureRoundSyncEvent?.({
      event: "round edit queued offline",
      properties: { round_id: this.aggregateId, action_type: actionType },
    });
  }

  /** The transport needed more than one attempt, or scheduled its own resend. */
  public noteTransportRetried(actionId: string): void {
    this._noteRetransmission(actionId);
  }

  /**
   * Report how long this client waited between sending an action and seeing it
   * confirmed. No-op for actions we never tracked, and for duplicate confirms
   * (the entry is consumed on the first one).
   */
  private _reportActionConfirmed(action: ActionMessage): void {
    const pending = this.actionSends.get(action.id);
    if (pending == null) return;
    this.actionSends.delete(action.id);

    this.captureRoundSyncEvent?.({
      event: "round action confirmed",
      properties: {
        round_id: this.aggregateId,
        action_type: action.payload.type,
        latency_ms: Math.round(performance.now() - pending.sentAt),
        // True when the wait spans at least one failed, suppressed or repeated
        // transmission — from ANY of the five send paths or the transport itself.
        // Read those separately from steady-state latency.
        was_resent: pending.retried,
        is_impersonating: this.actingAsUserId !== this.underlyingUserId,
      },
    });
  }

  public async performAction(
    action: StampableAction,
    actionId?: string,
  ): Promise<Error | UnconfirmedActionMessage> {
    const actionMsg: UnconfirmedActionMessage = {
      id: actionId ?? generateActionId(),
      aggregateId: this.aggregateId,
      // Creation-time stamp travels IN the payload so the optimistic apply,
      // the server's persisted copy, and every future replay use one value.
      payload: stampActionTimestamp(action),
      actingAsUserId: this.actingAsUserId,
      underlyingUserId: this.underlyingUserId,
      createdBySfcAdmin: this.underlyingUserIsSfcAdmin,
      undoes: null,
      redoes: null,
    };

    actionSyncInfo("[action-sync][client] performAction created", {
      actionId: actionMsg.id,
      actionType: actionMsg.payload.type,
      aggregateId: actionMsg.aggregateId,
      actingAsUserId: actionMsg.actingAsUserId,
      underlyingUserId: actionMsg.underlyingUserId,
      undoes: actionMsg.undoes,
      redoes: actionMsg.redoes,
    });

    const result = await this.storage.performUnconfirmedAction(
      this.reducer,
      actionMsg,
    );

    if (result instanceof Error) {
      console.error("[action-sync][client] performAction local apply failed", {
        actionId: actionMsg.id,
        actionType: actionMsg.payload.type,
        aggregateId: actionMsg.aggregateId,
        error: result,
      });
      return result;
    }

    this.actionsSincePageLoad.add(actionMsg.id);

    if (this.offlineMode) {
      actionSyncInfo(
        "[action-sync][client] performAction kept local because offline mode",
        {
          actionId: actionMsg.id,
          actionType: actionMsg.payload.type,
          aggregateId: actionMsg.aggregateId,
        },
      );
      return actionMsg;
    }

    if (!this.sendMessage) {
      console.warn("[action-sync][client] performAction sendMessage missing", {
        actionId: actionMsg.id,
        actionType: actionMsg.payload.type,
        aggregateId: actionMsg.aggregateId,
      });
      return new Error(`Client ${this.id} is not connected`);
    }

    this.onActionCallback?.(actionMsg);
    this.updateCallback?.();

    this._sendPerformAction(actionMsg, "first");
    actionSyncInfo("[action-sync][client] performAction sent to server", {
      actionId: actionMsg.id,
      actionType: actionMsg.payload.type,
      aggregateId: actionMsg.aggregateId,
    });

    return actionMsg;
  }

  public async retryUnconfirmedActions(): Promise<Error | null> {
    if (this.offlineMode) {
      return null;
    }

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    if (this.offlineMode) {
      return new Error("Can't retry actions in offline mode");
    }

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    const unconfirmedActions = await this.storage.getUnconfirmedActions();
    actionSyncInfo("[action-sync][client] retrying unconfirmed actions", {
      actionCount: unconfirmedActions.length,
      actionIds: unconfirmedActions.map((a) => a.message.id),
    });
    for (const action of unconfirmedActions) {
      this._sendPerformAction(action.message, "repeat");
      actionSyncInfo("[action-sync][client] resent unconfirmed action", {
        actionId: action.message.id,
        actionType: action.message.payload.type,
        aggregateId: action.message.aggregateId,
      });
    }

    // TODO: request all actions by ID to see if they're already confirmed

    return null;
  }

  public async clearUnconfirmedActions() {
    await this.storage.clearUnconfirmedActions();
    this.updateCallback?.();
  }

  public register() {
    const lastConfirmedVersion = this.storage.getLastConfirmedVersion();
    if (!this.sendMessage) {
      throw new Error(`Client ${this.id} is not connected`);
    }
    this.sendMessage({
      type: "Register",
      lastConfirmedVersion,
      supportsActionAck: true,
    });
    actionSyncInfo("[action-sync][client] register sent", {
      aggregateId: this.aggregateId,
      clientId: this.id,
      lastConfirmedVersion,
      supportsActionAck: true,
    });

    // Best-effort resend of any unconfirmed actions after (re)registration.
    // Socket.io typically buffers emits across reconnects, but in some
    // environments (tests with network proxies) messages can be lost. This
    // explicit resend is idempotent due to server-side deduplication by id.
    void this.sendUnconfirmedActions();
  }

  handleMessage(msg: MessageToClient): Promise<void> {
    actionSyncInfo("[action-sync][client] message received from server", {
      aggregateId: this.aggregateId,
      clientId: this.id,
      ...summarizeMessageToClient(msg),
    });

    return new Promise<void>((resolve) => {
      this.actionQueue.push({
        type: "handleMessage",
        msg,
        resolve,
      });

      void this._startNextPendingAction();
    });
  }

  async _handleMessage(msg: MessageToClient) {
    switch (msg.type) {
      case "PerformActionFailed":
        await this._handlePerformActionFailed(msg);
        break;
      case "ConfirmActions":
        await this._handleConfirmAction(msg);
        break;
      default:
        assertUnreachable(msg);
    }
  }

  private async _handlePerformActionFailed(msg: PerformActionFailed) {
    const transient =
      msg.message.includes(
        "not found in database after confirmation attempt",
      ) || msg.message.includes("Connection terminated unexpectedly");

    if (transient) {
      const actionId = msg.actionId;
      if (!this.retriedFailedActionIds.has(actionId)) {
        if (!this.sendMessage) {
          this.onErrorCallback(msg.message);
          this.updateCallback?.();
          return;
        }

        this.retriedFailedActionIds.add(actionId);
        console.warn(
          "[action-sync][client] transient PerformActionFailed, retrying once",
          {
            actionId,
            aggregateId: this.aggregateId,
            errorMessage: msg.message,
          },
        );
        void (async () => {
          const action = await this.storage.getAction(actionId);
          if (!action || !this.sendMessage) {
            console.error(
              "[action-sync][client] retry aborted because action/sendMessage missing",
              {
                actionId,
                aggregateId: this.aggregateId,
                hasAction: action != null,
                hasSendMessage: this.sendMessage != null,
              },
            );
            this.onErrorCallback(msg.message);
            this.updateCallback?.();
            return;
          }

          this._sendPerformAction(action, "repeat");
          actionSyncInfo("[action-sync][client] resent failed action", {
            actionId,
            aggregateId: this.aggregateId,
            actionType: action.payload.type,
          });
          this.updateCallback?.();
        })();
        return;
      }
    }

    console.error("[action-sync][client] PerformActionFailed not retried", {
      actionId: msg.actionId,
      aggregateId: this.aggregateId,
      transient,
      errorMessage: msg.message,
    });
    if (!transient) {
      let removed = false;
      try {
        await this.storage.removeUnconfirmedAction(this.reducer, msg.actionId);
        removed = true;
        actionSyncInfo(
          "[action-sync][client] removed failed unconfirmed action",
          {
            actionId: msg.actionId,
            aggregateId: this.aggregateId,
          },
        );
      } catch (error) {
        console.error("[action-sync][client] failed to remove failed action", {
          actionId: msg.actionId,
          aggregateId: this.aggregateId,
          error,
        });
        Sentry.captureException(error);
      }
      // Always log the server rejection. The user-facing message depends on
      // whether we actually dropped the action: only claim it was "discarded"
      // when it really left the durable queue. If removal failed the edit is
      // still queued (and would resend), so a "discarded" notice would be a lie
      // — surface a generic save-failure instead.
      Sentry.captureMessage(
        `[action-sync] PerformAction rejected by server: ${msg.message}`,
        { level: "warning" },
      );
      this.onErrorCallback(
        removed
          ? discardedActionMessage(msg.message)
          : `Couldn't save your change: ${msg.message}`,
      );
      this.updateCallback?.();
      return;
    }
    this.onErrorCallback(msg.message);
    this.updateCallback?.();
  }

  private async _handleConfirmAction(msg: ConfirmAction) {
    const unconfirmedActions = await this.storage.getUnconfirmedActions();
    const lastConfirmedVersion = this.storage.getLastConfirmedVersion();
    actionSyncInfo("[action-sync][client] processing ConfirmActions", {
      aggregateId: this.aggregateId,
      actionCount: msg.actions.length,
      firstActionId: msg.actions[0]?.id,
      lastActionId: msg.actions[msg.actions.length - 1]?.id,
      firstActionVersion: msg.actions[0]?.version,
      lastActionVersion: msg.actions[msg.actions.length - 1]?.version,
      lastConfirmedVersionBefore: lastConfirmedVersion,
      unconfirmedActionCountBefore: unconfirmedActions.length,
      unconfirmedActionIdsBefore: unconfirmedActions.map((a) => a.message.id),
    });

    for (const action of msg.actions) {
      // Before the version branch: a confirmation is a confirmation whether or
      // not we had already applied it. Duplicate confirms are harmless — the
      // first one consumed the pending entry.
      this._reportActionConfirmed(action);

      if (action.version <= lastConfirmedVersion) {
        const previousAction = await this.storage.getAction(action.id);
        if (
          previousAction &&
          !actionIsIdentical(previousAction.payload, action.payload)
        ) {
          if (
            action.payload.type === "EVALUATION_NOTES_CHANGE" ||
            action.payload.type === "EVALUATION_FEEDBACK_CHANGE" ||
            action.payload.type === "GENERAL_NOTES_CHANGE"
          ) {
            // These actions are track-anonymized on the broadcast stream, so the
            // same action can come back redacted vs. not (or change when sharing
            // settings change). A differing payload here is expected, not a bug.
            continue;
          }
          // Duplicate confirms should never block the ack loop. If an already
          // applied action comes back with a different payload, surface the
          // inconsistency but continue so we still acknowledge and avoid
          // infinite server re-broadcasts.
          console.error(
            `Previous action ${action.id} is not identical to the action from the server`,
          );
          console.error("previous action payload", previousAction.payload);
          console.error("action payload", action.payload);
          Sentry.withScope((scope) => {
            scope.setLevel("error");
            scope.setTag("sync_issue", "duplicate_confirm_payload_mismatch");
            scope.setTag("client_id", this.id);
            scope.setTag("aggregate_id", this.aggregateId);
            scope.setExtra("actionId", action.id);
            scope.setExtra("actionType", action.payload.type);
            scope.setContext("payloads", {
              previous: previousAction.payload,
              incoming: action.payload,
            });
            scope.setFingerprint([
              "s-process",
              "duplicate-confirm-payload-mismatch",
              action.payload.type,
            ]);
            Sentry.captureMessage(
              "Action payload mismatch for already confirmed action",
            );
          });
          this.onErrorCallback(
            `Action payload mismatch for already confirmed action ${action.id}`,
          );
        }

        continue;
      }

      const error = await this.storage.performConfirmedAction(
        this.reducer,
        action,
      );

      if (!error) {
        continue;
      }

      // Storage adapters convert internal throws into RETURNED Errors so a
      // storage fault can't jam the pump — but that boundary must not swallow
      // the stall signal. A StallError (a catch-up fetch that exhausted its
      // timeout) means "stop this message and open the stall episode", not
      // "note the failure and keep applying": rethrow it so the pump's
      // handler latches the episode and re-deliveries run under the probe
      // window instead of each paying the full fetch timeout.
      if (error instanceof StallError) {
        throw error;
      }

      if (error instanceof Error) {
        const lastConfirmedVersionAfter =
          this.storage.getLastConfirmedVersion();
        console.error("[action-sync][client] performConfirmedAction failed", {
          actionId: action.id,
          actionType: action.payload.type,
          actionVersion: action.version,
          aggregateId: this.aggregateId,
          error,
        });
        Sentry.withScope((scope) => {
          scope.setLevel("error");
          scope.setTag("sync_issue", "confirmed_action_apply_failed");
          scope.setTag("client_id", this.id);
          scope.setTag("aggregate_id", this.aggregateId);
          scope.setTag("action_type", action.payload.type);
          scope.setContext("action_sync", {
            aggregateId: this.aggregateId,
            clientId: this.id,
            actionId: action.id,
            actionType: action.payload.type,
            actionVersion: action.version,
            lastConfirmedVersionBefore: lastConfirmedVersion,
            lastConfirmedVersionAfter,
            batchActionCount: msg.actions.length,
            batchFirstActionId: msg.actions[0]?.id,
            batchLastActionId: msg.actions[msg.actions.length - 1]?.id,
            batchFirstActionVersion: msg.actions[0]?.version,
            batchLastActionVersion:
              msg.actions[msg.actions.length - 1]?.version,
            unconfirmedActionCountBefore: unconfirmedActions.length,
          });
          scope.setFingerprint([
            "s-process",
            "confirmed-action-apply-failed",
            action.payload.type,
          ]);
          Sentry.captureException(error);
        });
        this.onErrorCallback(error.message);
      } else {
        for (const e of error) {
          console.error(e);
          console.error(
            "[action-sync][client] performConfirmedAction returned action error",
            {
              actionId: e.actionId,
              aggregateId: this.aggregateId,
              error: e.error,
            },
          );
          this.onErrorCallback(e.error.message);
        }
      }
    }

    for (const action of msg.actions) {
      this.onActionConfirmedCallback?.(action);
      if (!unconfirmedActions.some(({ message }) => message.id === action.id)) {
        this.onActionCallback?.(action);
      }
    }

    if (this.sendMessage) {
      const acknowledgedVersion = this.storage.getLastConfirmedVersion();
      this.sendMessage({
        type: "AcknowledgeActions",
        lastConfirmedVersion: acknowledgedVersion,
      });
      actionSyncInfo("[action-sync][client] sent AcknowledgeActions", {
        aggregateId: this.aggregateId,
        lastConfirmedVersion: acknowledgedVersion,
      });
    }

    this.updateCallback?.();
  }

  connect(
    sendMessage: (msg: MessageToServer) => void,
    setMessageHandler: (
      handler: (msg: MessageToClient) => Promise<void>,
    ) => void,

    // To be called when the storage adapter has been updated by an action
    updateCallback?: () => void,

    // To be called with confirmed *or* unconfirmed actions, *before* updateCallback.
    // We always call updateCallback after this because in ClientWrapper we want
    // to fill the queue of actions (pendingActions) which is cleared by the render
    // triggered by updateCallback.
    onActionCallback?: (action: UnconfirmedActionMessage) => void,
  ) {
    this.sendMessage = sendMessage;
    setMessageHandler(this.handleMessage.bind(this));
    this.updateCallback = updateCallback;
    this.onActionCallback = onActionCallback;
  }

  // This is intended to be used on startup in case there are any actions that
  // weren't synced before the user closed their tab.
  async sendUnconfirmedActions(): Promise<Error | void> {
    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    let unconfirmedActions: {
      createdAt: number;
      message: IncomingUnconfirmedActionMessage;
    }[];
    try {
      unconfirmedActions = await this.storage.getUnconfirmedActions();
    } catch (error) {
      if (!isTransientIndexedDbDisconnectError(error)) throw error;
      // One re-read after a beat. The transient class is a request caught
      // by an iOS suspend or a dropped connection; on the device the very
      // next request after such a failure succeeded every time, so a single
      // re-read completes the drain almost always, and this is the one read
      // whose loss the funder would notice (queued edits waiting for the
      // next reconnect).
      await new Promise((resolve) =>
        setTimeout(resolve, TRANSIENT_IDB_REREAD_DELAY_MS),
      );
      try {
        unconfirmedActions = await this.storage.getUnconfirmedActions();
        actionSyncInfo(
          "[action-sync][client] queue re-read succeeded after a transient IndexedDB failure",
          { aggregateId: this.aggregateId, clientId: this.id },
        );
      } catch (retryError) {
        // Re-classify: the re-read is a second chance for the transient
        // class only. A different failure here (quota, a corrupt database)
        // is the same event it would be on a first read and takes the same
        // path — it must not inherit the transient downgrade.
        if (!isTransientIndexedDbDisconnectError(retryError)) throw retryError;
        const capturedError =
          retryError instanceof Error
            ? retryError
            : new Error("Transient IndexedDB disconnect");
        console.warn(
          "[action-sync][client] transient IndexedDB failure reading the queue for the reconnect resend; the queue is intact and resends on the next reconnect",
          {
            aggregateId: this.aggregateId,
            clientId: this.id,
            error: retryError,
          },
        );
        Sentry.withScope((scope) => {
          // A known-transient class (see indexedDbErrors.ts): the queued
          // edits are still durable and go out on the next reconnect, so
          // this is worth a record, not a page.
          scope.setLevel("warning");
          scope.setTag("sync_issue", "indexeddb_disconnect_resend_failed");
          scope.setTag("aggregate_id", this.aggregateId);
          scope.setTag("client_id", this.id);
          Sentry.captureException(capturedError);
        });
        return capturedError;
      }
    }
    actionSyncInfo(
      "[action-sync][client] sending startup unconfirmed actions",
      {
        aggregateId: this.aggregateId,
        actionCount: unconfirmedActions.length,
        actionIds: unconfirmedActions.map((a) => a.message.id),
      },
    );
    for (const action of unconfirmedActions) {
      // The reconnect drain. Previously the only send path with no transmission
      // accounting, so an edit that survived a disconnect confirmed as a clean
      // steady-state save.
      this._sendPerformAction(action.message, "repeat");
      actionSyncInfo("[action-sync][client] sent startup unconfirmed action", {
        aggregateId: this.aggregateId,
        actionId: action.message.id,
        actionType: action.message.payload.type,
      });
    }
  }

  _undo(): Promise<Error | UndoRedoInfo | null> {
    return this._undoOrRedo("UNDO");
  }

  _redo(): Promise<Error | UndoRedoInfo | null> {
    return this._undoOrRedo("REDO");
  }

  async _undoOrRedo(
    type: "UNDO" | "REDO",
  ): Promise<Error | UndoRedoInfo | null> {
    const actions = [
      ...this.storage.getConfirmedActions(),
      ...(await this.storage.getUnconfirmedActions()).map(
        ({ message }) => message,
      ),
    ];

    let actionMessage: UnconfirmedActionMessage;
    let originalAction: IncomingUnconfirmedActionMessage;

    if (type === "UNDO") {
      const undoer = generateUndoAction(actions, this.actingAsUserId);
      if (!undoer) {
        return null;
      }

      originalAction = undoer.undoneAction as IncomingUnconfirmedActionMessage;
      if (!this.actionsSincePageLoad.has(originalAction.id)) {
        return null;
      }

      let aggregateBefore;
      if (
        "version" in originalAction &&
        typeof originalAction.version === "number"
      ) {
        // If it's a confirmed action
        aggregateBefore = await this.storage.getAggregateAtVersion(
          this.reducer,
          originalAction.version - 1,
        );
        if (aggregateBefore === null) {
          return new Error(
            `Undone action ${originalAction.id} not found at version ${originalAction.version - 1}`,
          );
        }
      } else {
        // If it's an unconfirmed action
        aggregateBefore =
          await this.storage.getAggregateBeforeUnconfirmedAction(
            this.reducer,
            originalAction.id,
          );
        if (aggregateBefore instanceof Error) {
          return aggregateBefore;
        }
      }

      const undoOrRedoAction = undoer.makeAction(aggregateBefore);
      actionMessage = {
        aggregateId: this.aggregateId,
        id: generateActionId(),
        payload: stampActionTimestamp(undoOrRedoAction),
        underlyingUserId: this.underlyingUserId,
        actingAsUserId: this.actingAsUserId,
        undoes: originalAction.id,
        redoes: null,
      };
    } else {
      // REDO
      const redoer = generateRedoAction(actions, this.actingAsUserId);
      if (!redoer) {
        return null;
      }
      const { undoes, redoes } = redoer;
      const possibleOriginalAction = await this.storage.getAction(redoes);
      if (!possibleOriginalAction) {
        return new Error(`Original action ${redoes} not found`);
      }
      originalAction = possibleOriginalAction;
      if (!this.actionsSincePageLoad.has(redoes)) {
        return null;
      }

      const originalActionMessage = await this.storage.getAction(redoes);
      if (!originalActionMessage) {
        return new Error(`Original action ${redoes} not found`);
      }
      actionMessage = {
        aggregateId: this.aggregateId,
        id: generateActionId(),
        // REDO reuses the original payload but is a new touch happening now —
        // fresh overrides the original action's creation stamp.
        payload: stampActionTimestamp(originalActionMessage.payload, {
          fresh: true,
        }),
        underlyingUserId: this.underlyingUserId,
        actingAsUserId: this.actingAsUserId,
        undoes,
        redoes,
      };
    }

    const nullOrError = await this.storage.performUnconfirmedAction(
      this.reducer,
      actionMessage,
    );

    if (nullOrError instanceof Error) {
      return nullOrError;
    }

    this.actionsSincePageLoad.add(actionMessage.id);

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    this.updateCallback?.();
    this.onActionCallback?.(actionMessage);
    this._sendPerformAction(actionMessage, "first");

    return { message: actionMessage, originalAction };
  }

  getAggregate() {
    return this.storage.getUnconfirmedAggregateSync();
  }

  async dispatchUndo(): Promise<Error | UndoRedoInfo | null> {
    return new Promise<Error | UndoRedoInfo | null>((resolve) => {
      this.actionQueue.push({ type: "undo", resolve });
      void this._startNextPendingAction();
    });
  }

  async dispatchRedo(): Promise<Error | UndoRedoInfo | null> {
    return new Promise<Error | UndoRedoInfo | null>((resolve) => {
      this.actionQueue.push({ type: "redo", resolve });
      void this._startNextPendingAction();
    });
  }

  async dispatch(
    action: StampableAction,
  ): Promise<Error | UnconfirmedActionMessage> {
    // Returns a promise that resolves when the action's turn comes up in the
    // queue and it finishes, which is handled by storing the promise itself
    // in the action queue.
    return new Promise<Error | UnconfirmedActionMessage>((resolve) => {
      this.actionQueue.push({ type: "action", action, resolve });
      void this._startNextPendingAction();
    });
  }

  private async _startNextPendingAction() {
    if (this.isActionPending) {
      return;
    }

    const actionToPerform = this.actionQueue.shift();
    if (!actionToPerform) {
      this.isActionPending = false;
      return;
    }

    this.isActionPending = true;
    try {
      switch (actionToPerform.type) {
        case "action": {
          const { action, resolve } = actionToPerform;
          const result = await this.performAction(action);
          resolve(result);
          break;
        }
        case "undo": {
          const { resolve } = actionToPerform;
          const result = await this._undo();
          resolve(result);
          break;
        }
        case "redo": {
          const { resolve } = actionToPerform;
          const result = await this._redo();
          resolve(result);
          break;
        }
        case "handleMessage": {
          const { msg, resolve } = actionToPerform;
          // An await inside message handling that never settles — a
          // black-holed catch-up fetch, a Safari IndexedDB request that never
          // calls back — would otherwise hold this serial pump forever: the
          // user's own edits queue behind it unsent and unsaved, with no
          // error anywhere. The guard trips only on a window with zero
          // confirmed-version advance, so slow-but-progressing catch-ups run
          // to completion. On a stall the orphaned work keeps running (it
          // cannot be cancelled) but the pump is freed; no ack is sent for
          // anything unapplied, so the server re-delivers and the
          // already-applied prefix short-circuits on retry.
          let versionAtLastCheck = this.storage.getLastConfirmedVersion();
          let activityAtLastCheck = getSyncActivityCount();
          try {
            await withStallGuard(this._handleMessage(msg), {
              windowMs: this.stallEpisode.windowFor(versionAtLastCheck),
              // Progress = the confirmed version advanced OR the catch-up
              // network layer showed liveness (see syncActivityBeacon): a gap
              // download runs before any version advance, so version alone
              // would misread a slow-but-alive fetch as a stall. A true hang
              // moves neither signal.
              hasProgressed: () => {
                const version = this.storage.getLastConfirmedVersion();
                const activity = getSyncActivityCount();
                const progressed =
                  version > versionAtLastCheck ||
                  activity !== activityAtLastCheck;
                versionAtLastCheck = version;
                activityAtLastCheck = activity;
                return progressed;
              },
              label: "[action-sync][client] message processing",
            });
          } catch (error) {
            if (error instanceof StallError) {
              this._reportProcessingStall(error);
            } else {
              throw error;
            }
          } finally {
            if (resolve) resolve();
          }
          break;
        }
        default:
          assertUnreachable(actionToPerform);
      }
    } catch (error) {
      // A throw from storage or message handling must fail THIS item, not the
      // pump. Storage adapters return their errors, but runtime IndexedDB
      // faults (disk full, the browser's IndexedDB backend dying mid-session)
      // and network failures inside confirmed-action catch-up can still
      // reject; without this catch, `isActionPending` sticks true and every
      // later edit, undo/redo, and server confirmation queues forever behind
      // the failed item, with the caller's dispatch promise hanging.
      const failure = error instanceof Error ? error : new Error(String(error));
      console.error("[action-sync][client] action pump item threw", {
        aggregateId: this.aggregateId,
        clientId: this.id,
        itemType: actionToPerform.type,
        error: failure,
      });
      Sentry.withScope((scope) => {
        scope.setTag("sync_issue", "action_pump_item_threw");
        scope.setTag("client_id", this.id);
        scope.setTag("aggregate_id", this.aggregateId);
        scope.setExtra("itemType", actionToPerform.type);
        Sentry.captureException(failure);
      });
      // Settle the caller BEFORE notifying, so a throwing error callback can
      // never leave the dispatch promise hanging.
      if (actionToPerform.type === "handleMessage") {
        actionToPerform.resolve?.();
      } else {
        actionToPerform.resolve(failure);
      }
      this.onErrorCallback(failure.message);
    } finally {
      this.isActionPending = false;
      void this._startNextPendingAction();
    }
  }

  // A stalled message is deliberately NOT acknowledged — the actions were not
  // applied, and claiming progress would diverge from the server. Report once
  // per episode (the tracker's version-lazy latch decides; late recovery on a
  // path the pump never sees still closes it) and tell the user how to
  // recover; the reload rebuilds a clean session, and everything the user
  // keeps doing meanwhile still reaches the server (the pump is free again).
  private _reportProcessingStall(error: StallError) {
    console.error(
      "[action-sync][client] message processing stalled; queue recovered",
      {
        aggregateId: this.aggregateId,
        clientId: this.id,
        error,
      },
    );
    if (!this.stallEpisode.open(this.storage.getLastConfirmedVersion())) {
      return;
    }
    Sentry.withScope((scope) => {
      scope.setLevel("error");
      scope.setTag("sync_issue", "message_processing_stalled");
      scope.setTag("client_id", this.id);
      scope.setTag("aggregate_id", this.aggregateId);
      scope.setFingerprint(["s-process", "message-processing-stalled"]);
      Sentry.captureException(error);
    });
    this.onErrorCallback(
      "We couldn't sync your latest changes — your browser may have a stuck connection or storage. Please reload the page.",
    );
    this.updateCallback?.();
  }
}

type Undoer = {
  // We need the state of the aggregate before the original action
  // so we know what state to revert back to.
  makeAction: (roundBeforeAction: RoundAggregate) => StampableAction;
  undoneAction: IncomingActionMessage | IncomingUnconfirmedActionMessage;
};

export function generateUndoAction(
  actions: IncomingUnconfirmedActionMessage[],
  userId: string,
): Undoer | null {
  const undoneActionIds: Set<string> = new Set();
  const undosToSkip: Set<string> = new Set();

  for (let i = actions.length - 1; i >= 0; i--) {
    const action = actions[i];
    if (action == null) {
      continue;
    }

    if (action.actingAsUserId !== userId) {
      continue;
    }

    // action is a redo
    if (action.redoes !== null && action.undoes !== null) {
      undosToSkip.add(action.undoes);
      continue;
    }

    // action is an undo
    if (action.redoes === null && action.undoes !== null) {
      if (!undosToSkip.has(action.id)) {
        undoneActionIds.add(action.undoes);
      }
      continue;
    }

    if (!undoneActionIds.has(action.id)) {
      const maybeUndoFn = undoAction(action.payload);
      if (maybeUndoFn) {
        return {
          makeAction: maybeUndoFn,
          undoneAction: action,
        };
      }
    }
  }

  return null;
}

export function generateRedoAction(
  actions: IncomingUnconfirmedActionMessage[],
  userId: string,
): { undoes: string; redoes: string } | null {
  const redoneActionIds: Set<string> = new Set();

  for (let i = actions.length - 1; i >= 0; i--) {
    const action = actions[i];
    if (action == null) {
      continue;
    }

    if (action.actingAsUserId !== userId) {
      continue;
    }

    // action is a redo
    if (action.undoes !== null && action.redoes !== null) {
      // Note that this line is tricky but action.undoes (not action.redoes) is
      // the correct action. We use redoneActionIds to skip over the undo that
      // was canceled out. We will already ignore the original action
      // (action.redoes) because it's not an undo.
      redoneActionIds.add(action.undoes);
      continue;
    }

    // action is an undo
    if (
      action.undoes !== null &&
      action.redoes === null &&
      !redoneActionIds.has(action.id)
    ) {
      return {
        undoes: action.id,
        redoes: action.undoes,
      };
    }
  }

  return null;
}
